import React from 'react';
import { useCallback, useMemo } from 'react';
import { SurveyInstance, SurveyProps } from './Survey.types';
import { createSurveyConfig, createSurveyInstance } from './Survey.utils';
import { useSurvey } from './Survey.provider';
import { StyledSurveyButtonWrapper, StyledSurveyChexboxListWrapper, StyledSurveyContent, StyledSurveyError, StyledSurveyTitle, StyledSurveyWrapper } from './Survey.styles';
import { Box, ButtonV2, Flex } from '@withjoy/joykit';

import { pxToRem, useTheme } from '@withjoy/joykit/theme';
import { StyledControl, StyledLabel } from '@withjoy/joykit/components/CheckboxV2/Checkbox.styles';
import { StyledTextArea } from '@withjoy/joykit/components/TextArea/StyledTextArea';
import { useTranslation } from '@shared/core';
import { useSurveyTelemetry } from './Survey.telemetry';

const SurveyRoot: React.FC<
  {
    surveyInstance: SurveyInstance;
  } & Pick<SurveyProps, 'onSkip' | 'onFirstStepBack' | 'icon' | 'styleOverrides' | 'telemetryCategory' | 'telemetryPagePrefix'>
> = ({ surveyInstance, onSkip, onFirstStepBack, icon, styleOverrides, telemetryCategory, telemetryPagePrefix }) => {
  const { colors } = useTheme();
  const { t } = useTranslation('survey');
  const buttonsTranslations = t('buttons');

  const telemetry = useSurveyTelemetry();
  const { currentStep, answers, config, hasNextStep } = useSurvey();

  const handleSkip = useCallback(() => {
    onSkip?.(answers);
    telemetry.surveyCompleted(config.name, currentStep, answers, telemetryCategory, telemetryPagePrefix, true);
  }, [answers, config.name, currentStep, onSkip, telemetry, telemetryCategory, telemetryPagePrefix]);

  const { Renderer: SurveyRenderer } = surveyInstance;

  return (
    <Flex
      paddingX={[3, 9]}
      paddingY={9}
      width={'100%'}
      __css={{
        alignItems: 'center',
        flexDirection: 'column',
        [StyledSurveyWrapper]: {
          paddingX: pxToRem(19),
          rowGap: 5,
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        },
        [StyledSurveyContent]: {
          paddingX: pxToRem(19),
          rowGap: 5,
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        },
        [StyledSurveyTitle]: {
          typographyVariant: 'hed4',
          textAlign: 'center',
        },
        [StyledSurveyButtonWrapper]: {
          width: '100%',
          columnGap: 6,
          paddingTop: 6,
        },
        [StyledSurveyError]: {
          typographyVariant: 'body1',
          marginBottom: -6,
          paddingTop: 6,
        },
        [StyledSurveyChexboxListWrapper]: {
          rowGap: 5,
        },
        [StyledLabel]: {
          typographyVariant: 'body1',
        },
        [StyledControl]: {
          backgroundColor: 'mono2',
          border: 0,
          color: `${colors.newUIBrandPrimary}!important`,
          _focus: {
            boxShadow: 'none',
          },
        },
        [StyledTextArea]: {
          _hover: {
            borderColor: `${colors.newUIBrandPrimary}!important`,
          },
        },
        ...styleOverrides,
      }}
    >
      {icon && icon}
      <SurveyRenderer
        nextButton={({ goNext, label }) => (
          <ButtonV2
            onClick={goNext}
            fullWidth
            shape={'rounded'}
            intent="newPrimary"
            {...(hasNextStep ? config.buttons?.nextProps : config.buttons?.submitProps)}
          >
            {label}
          </ButtonV2>
        )}
        backButton={({ goBack, label }) => (
          <ButtonV2
            onClick={goBack}
            fullWidth
            shape={'rounded'}
            intent="newPrimary"
            {...config.buttons?.backProps}
          >
            {label}
          </ButtonV2>
        )}
      />
      {currentStep !== config.initialStep && typeof onSkip === 'function' && (
        <Box
          paddingTop={5}
          width={'100%'}
        >
          <ButtonV2
            onClick={handleSkip}
            fullWidth
            variant={'ghost'}
            shape={'rounded'}
            intent={'neutral'}
            {...config.buttons?.skipProps}
          >
            {config.buttons?.skipLabel || buttonsTranslations.skipLabel()}
          </ButtonV2>
        </Box>
      )}
    </Flex>
  );
};

export const Survey: React.FC<SurveyProps> = ({ onComplete, config, onSkip, onFirstStepBack, icon, styleOverrides, telemetryCategory, telemetryPagePrefix }) => {
  const handleComplete = useCallback(
    (answers: Record<string, unknown>) => {
      onComplete?.(answers);
    },
    [onComplete],
  );

  const telemetryConfig = useMemo(
    () => ({
      category: telemetryCategory,
      pagePrefix: telemetryPagePrefix,
    }),
    [telemetryCategory, telemetryPagePrefix],
  );

  const Survey = useMemo(
    () =>
      createSurveyInstance({
        config: createSurveyConfig(config),
        onFirstStepBack,
        onComplete: handleComplete,
        telemetryConfig,
      }),
    [config, onFirstStepBack, handleComplete, telemetryConfig],
  );

  return (
    <Survey.Provider>
      <SurveyRoot
        surveyInstance={Survey}
        onSkip={onSkip}
        onFirstStepBack={onFirstStepBack}
        icon={icon}
        styleOverrides={styleOverrides}
        telemetryCategory={telemetryCategory}
        telemetryPagePrefix={telemetryPagePrefix}
      />
    </Survey.Provider>
  );
};
